import { useOrderBumbalData } from "app/hooks/appointment/useOrderBumbalData";
import { useOrderContext } from "app/providers/OrderProvider";
import StatusGif from "entities/status-gif";
import moment from "moment";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Confetti from "shared/ui/confetti";
import CustomLink from "shared/ui/typography/CustomLink";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import { isDevelopmentEnvironment } from "shared/utils/checkEnvironment";
import { getLocalDateString } from "shared/utils/date";
import { twMerge } from "tailwind-merge";
import OrderLoader from "widgets/shared/order-loader";
import StatusSupport from "widgets/status-support";
import { StandardOrderAppointmentCreated } from "./StandardOrderAppointmentCreated";

const APPOINTMENT_TYPES: Record<
  string,
  { title: string; description: string; subtitle: string }
> = {
  measurement: {
    title: "GewoonGers komt naar je toe!",
    subtitle: "Leuk! We komen binnenkort bij je langs.",
    description: `Jouw voorkeuren zijn bij ons bekend. Je ontvangt hiervan een 
      bevestiging per mail. Hou er met het plannen rekening mee dat de situatie waarin 
      jouw wand of deur wordt geplaatst helemaal gereed is voor inmeten. Denk hierbij aan 
      het leggen van een nieuwe vloer, het stuken van de wand en een definitieve afwerking 
      van de sparing of het kozijn. Neem bij twijfel contact op met onze klantenservice en 
      hou altijd je referentienummer bij de hand.`,
  },
  shipment: {
    title: "Je afleverafspraak is gemaakt!",
    subtitle: "Leuk! We komen binnenkort bij je langs.",
    description: `Jouw voorkeuren zijn bij ons bekend. Je ontvangt hiervan een 
      bevestiging per mail. Op de afgesproken datum en tijd komen wij de 
      zelfmontage deur bij je afleveren.`,
  },
  placement: {
    title: "GewoonGers komt naar je toe!",
    subtitle: "",
    description: "",
  },
};

const AppointmentCreated = () => {
  const { order, type, isOrderFetching, orderError } = useOrderContext();
  const { orderBumbalData } = useOrderBumbalData();
  const [searchParams] = useSearchParams();
  const measurementDate = useMemo(
    () => new Date(order?.measurementDate || ""),
    [order],
  );

  const deliveryDate = useMemo(
    () => new Date(order?.deliveryDate || ""),
    [order],
  );

  const standardOrderId = searchParams.get("standardOrderId");

  const TypeBasedComponent = useMemo(() => {
    const yellowUnderlineClass =
      "gg-font-bold gg-underline gg-decoration-yellow-400 gg-decoration-2 gg-underline-offset-2";

    switch (type) {
      case "placement":
        return () => (
          <div className="gg-text-lg">
            <StatusGif
              src={`https://media1.giphy.com/media/ua4OxpbgAtItnS5hWu/giphy.gif`}
              alt="Een GewoonGers Gif. Een monteur springt uit zijn bus."
            />

            <Heading
              headingLevel="h1"
              className="gg-mb-3 md:gg-mb-10 md:gg-text-4xl lg:gg-text-7xl"
            >
              {APPOINTMENT_TYPES[type].title}
            </Heading>

            <Text className="gg-mb-3 gg-text-xl md:gg-mb-5 md:gg-text-2xl">
              Hallo {order?.shippingAddress.firstName}{" "}
              {order?.shippingAddress.lastName},
            </Text>

            {order !== undefined && (
              <Text className="gg-mb-3 md:gg-mb-5">
                Wij gaan voor je aan de slag en staan op{" "}
                <b>{getLocalDateString(deliveryDate)}</b> op{" "}
                <span className="gg-font-bold">
                  {order.shippingAddress.street}{" "}
                  {order.shippingAddress.houseNumber},{" "}
                  {order.shippingAddress.zip} in {order.shippingAddress.city}
                </span>{" "}
                om je bestelling te leveren en te installeren. Zet je 'm in de
                agenda?
              </Text>
            )}

            <Text className="gg-mb-2">Wat nu?</Text>
            <ul className="gg-mb-6 gg-grid gg-list-disc gg-gap-y-2 gg-pl-5">
              <li>
                Deze afspraak wordt bevestigd per e-mail.{" "}
                <span className={yellowUnderlineClass}>
                  Twee dagen van tevoren
                </span>
                , rond de klok van 18.00 uur, krijg je een tijdsblok van 2 uur
                toegestuurd waarin je ons kunt verwachten.
              </li>

              <li>
                Op de dag van de montage-afspraak, word je ongeveer{" "}
                <span className={yellowUnderlineClass}>
                  30 minuten van tevoren
                </span>{" "}
                gebeld dat we eraan komen. Je hoeft dus niet de hele dag thuis
                op ons te wachten!
              </li>

              <li>
                De monteurs gaan voor je aan de slag. Helpen is natuurlijk niet
                nodig. Zorg wel dat onze monteurs er goed bij kunnen.
              </li>

              <li>
                Na de montage reken je het resterende bedrag af, als je alleen
                een aanbetaling hebt gedaan. Onze monteurs delen een betaallink
                nadat alles naar wens is geïnstalleerd.
              </li>
            </ul>

            <Text className="gg-mb-10">
              <b>Let op:</b> zorg ervoor dat je je online{" "}
              <span className={yellowUnderlineClass}>
                betaallimiet vooraf verhoogt
              </span>{" "}
              als dat nodig is. De betaling moet succesvol worden afgerond
              voordat de monteurs de locatie verlaten.{" "}
            </Text>

            <Text>
              Heb je vragen of opmerkingen? Neem dan gerust even contact met ons
              op en houd je bevestigingsnummer of postcode bij de hand. Contact
              ons via:{" "}
              <CustomLink
                to="mailto:info@gewoongers.nl"
                opacityTransition={false}
              >
                info@gewoongers.nl
              </CustomLink>{" "}
              of{" "}
              <CustomLink to="tel:+310103072889" opacityTransition={false}>
                010 3072889
              </CustomLink>
              .
            </Text>
          </div>
        );
      case "measurement":
        return () => (
          <div className="gg-text-lg">
            <StatusGif
              src={`https://media1.giphy.com/media/XEzlPjgj6eCu3AZPhu/giphy.gif`}
              alt="Een GewoonGers Gif. Inmeter zwaait vanuit de auto."
            />

            <Heading
              headingLevel="h1"
              className="gg-mb-3 md:gg-mb-10 md:gg-text-4xl lg:gg-text-7xl"
            >
              {APPOINTMENT_TYPES[type].title}
            </Heading>

            <Text className="gg-mb-3 gg-text-xl md:gg-mb-5 md:gg-text-2xl">
              Hallo {order?.shippingAddress.firstName}{" "}
              {order?.shippingAddress.lastName},
            </Text>

            {order !== undefined && order.measurementDate !== undefined && (
              <Text className="gg-mb-3 md:gg-mb-5">
                Leuk dat we welkom zijn op{" "}
                <span className="gg-font-bold">
                  {order.shippingAddress.street}{" "}
                  {order.shippingAddress.houseNumber},{" "}
                  {order.shippingAddress.zip} in {order.shippingAddress.city}
                </span>
                . Op{" "}
                <span className="gg-font-bold">
                  {getLocalDateString(measurementDate)}
                </span>{" "}
                komen we langs voor gratis inmeten en advies. Zet je 'm in de
                agenda?
              </Text>
            )}

            <Text className="gg-mb-2">Wat nu?</Text>
            <ul className="gg-mb-6 gg-grid gg-list-disc gg-gap-y-2 gg-pl-5">
              <li>
                Deze afspraak wordt bevestigd per e-mail.{" "}
                <span className={yellowUnderlineClass}>
                  Twee dagen van tevoren
                </span>
                , rond de klok van 18.00 uur, krijg je een tijdsblok van 2 uur
                toegestuurd waarin je ons kunt verwachten.
              </li>

              <li>
                Op de dag van de inmeetafspraak, word je ongeveer{" "}
                <span className={yellowUnderlineClass}>30 minuten</span> van
                tevoren gebeld dat we eraan komen. Je hoeft dus niet de hele dag
                thuis op ons te wachten!
              </li>

              <li>
                Ter plaatste bespreken we je wensen en kijken mee in hoeverre
                deze mogelijk zijn. Ook{" "}
                <span className={yellowUnderlineClass}>
                  het inmeten wordt meteen gedaan.
                </span>
              </li>

              <li>
                De afspraak is{" "}
                <span className={yellowUnderlineClass}>
                  geheel vrijblijvend
                </span>
                . Achteraf bepaal jij of je over wilt gaan tot het bestellen van
                jouw nieuwe wand of deur. Wil je de bestelling bij ons plaatsen?
                Dan vragen wij in ieder geval een aanbetaling van 10%. Betalen
                kan via de betaallink die we je per e-mail zullen toesturen.
              </li>
            </ul>

            <Text>
              Heb je vragen of opmerkingen? Neem dan gerust even contact met ons
              op en houd je bevestigingsnummer of postcode bij de hand. Contact
              ons via:{" "}
              <CustomLink
                to="mailto:info@gewoongers.nl"
                opacityTransition={false}
              >
                info@gewoongers.nl
              </CustomLink>{" "}
              of{" "}
              <CustomLink to="tel:+310103072889" opacityTransition={false}>
                010 3072889
              </CustomLink>
              .
            </Text>
          </div>
        );
      default:
        return () => (
          <div>
            <StatusGif
              src={`https://media1.giphy.com/media/XEzlPjgj6eCu3AZPhu/giphy.gif`}
              alt="Een GewoonGers Gif. Inmeter zwaait vanuit de auto."
            />

            <Heading
              headingLevel="h1"
              className="gg-mb-3 md:gg-mb-10 md:gg-text-4xl lg:gg-text-7xl"
            >
              {APPOINTMENT_TYPES[type].title}
            </Heading>

            <Heading headingLevel={"h4"} className="gg-mb-3">
              {APPOINTMENT_TYPES[type].subtitle}
            </Heading>

            <Text className="gg-mb-4 gg-max-w-screen-md">
              {APPOINTMENT_TYPES[type].description}
            </Text>

            {!isOrderFetching && !orderError && !!order && (
              <Text>Referentienummer: {order?.orderNumber}</Text>
            )}

            <Text>
              Ingepland op: {moment(orderBumbalData?.date).format("DD-MM-YYYY")}
            </Text>
          </div>
        );
    }
  }, [order, type]);

  if (standardOrderId) {
    return <StandardOrderAppointmentCreated />;
  }
  return (
    <div
      className={twMerge(
        isDevelopmentEnvironment()
          ? "gg-px-4 gg-py-14 sm:gg-px-14 lg:gg-px-0"
          : "",
        "gg-mx-auto gg-max-w-3xl",
      )}
    >
      {!order && isOrderFetching ? (
        <OrderLoader />
      ) : (
        <>
          <Confetti />

          <div className="gg-mb-14">
            <TypeBasedComponent />
          </div>

          <StatusSupport />
        </>
      )}
    </div>
  );
};
export default AppointmentCreated;
